import React, { useState } from 'react';
import {
    Badge,
    Box,
    Chip,
    Collapse,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Comment, ExpandLess, ExpandMore } from '@mui/icons-material';
import CommentList from './CommentList';
import { LANGUAGES, LANGUAGE_NAMES } from '../constants/languages';

function TermList({
    terms = [],
    onTermSelect = () => {},
    selectedTermId,
    selectedLanguage,
    showSourceContext = false,
    onStatusChange = () => {},
}) {
    const [expandedTerm, setExpandedTerm] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [visibleLanguages, setVisibleLanguages] = useState(['en', 'da', 'no']);
    const rowsPerPage = 10;

    const handleExpandClick = (termId) => {
        setExpandedTerm((prev) => (prev === termId ? null : termId));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Confirmed':
                return 'rgba(46, 125, 50, 0.06)';
            case 'Outdated':
                return 'rgba(211, 47, 47, 0.06)';
            case 'Pending':
                return 'rgba(2, 136, 209, 0.06)';
            default:
                return 'transparent';
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' },
                    mb: 1.5,
                }}
            >
                <Typography variant="h6" fontWeight={700}>
                    Term list
                </Typography>

                <TextField
                    select
                    size="small"
                    label="Columns"
                    value={visibleLanguages}
                    onChange={(e) => {
                        const value = e.target.value;
                        setVisibleLanguages(value.length ? value : ['en']);
                    }}
                    sx={{ minWidth: { xs: '100%', sm: 300 } }}
                    SelectProps={{
                        multiple: true,
                        renderValue: (selected) => (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {selected.map((code) => (
                                    <Chip key={code} size="small" label={LANGUAGE_NAMES[code]} />
                                ))}
                            </Box>
                        ),
                    }}
                >
                    {LANGUAGES.map(({ code, name }) => (
                        <MenuItem key={code} value={code}>
                            {name}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {terms.length === 0 ? (
                <Typography color="text.secondary">No terms available.</Typography>
            ) : (
                <>
                    <TableContainer sx={{ maxHeight: 440 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                                    {visibleLanguages.map((lang) => (
                                        <TableCell key={lang} sx={{ fontWeight: 700 }}>
                                            {LANGUAGE_NAMES[lang]}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ fontWeight: 700, width: 150 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 90 }}>Comments</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {terms
                                    .slice(
                                        currentPage * rowsPerPage,
                                        currentPage * rowsPerPage + rowsPerPage
                                    )
                                    .map((term) => (
                                        <React.Fragment key={term?._id || `${term?.productName}-${currentPage}`}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    backgroundColor:
                                                        selectedTermId === term._id
                                                            ? 'action.selected'
                                                            : getStatusColor(term.status),
                                                }}
                                            >
                                                <TableCell>{term?.productName || 'N/A'}</TableCell>

                                                {visibleLanguages.map((lang) => {
                                                    const commentCount = term.translationComments?.[lang] || 0;
                                                    const isSelected =
                                                        selectedTermId === term._id && selectedLanguage === lang;
                                                    const englishTerm = term.translations?.en;

                                                    return (
                                                        <TableCell
                                                            key={`${term._id}-${lang}`}
                                                            onClick={() => term._id && onTermSelect(term._id, lang)}
                                                            sx={{
                                                                cursor: term._id ? 'pointer' : 'default',
                                                                bgcolor: isSelected ? 'action.selected' : 'transparent',
                                                                boxShadow: isSelected
                                                                    ? 'inset 3px 0 0 currentColor'
                                                                    : 'none',
                                                                color: isSelected ? 'primary.main' : 'inherit',
                                                                transition: 'background-color 120ms ease',
                                                            }}
                                                        >
                                                            <Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: isSelected ? 700 : 400,
                                                                            color: 'text.primary',
                                                                        }}
                                                                    >
                                                                        {term.translations?.[lang] || '—'}
                                                                    </Typography>
                                                                    {commentCount > 0 && (
                                                                        <Badge badgeContent={commentCount} color="primary">
                                                                            <Comment fontSize="small" />
                                                                        </Badge>
                                                                    )}
                                                                </Box>

                                                                {(showSourceContext || isSelected) && lang !== 'en' && englishTerm && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{
                                                                            display: 'block',
                                                                            mt: 0.45,
                                                                            lineHeight: 1.35,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            component="span"
                                                                            sx={{ fontWeight: 700, mr: 0.5 }}
                                                                        >
                                                                            EN ·
                                                                        </Box>
                                                                        {englishTerm}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    );
                                                })}

                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        value={term.status || ''}
                                                        onChange={(e) => onStatusChange(term._id, e.target.value)}
                                                        displayEmpty
                                                        fullWidth
                                                    >
                                                        <MenuItem value="">No status</MenuItem>
                                                        <MenuItem value="Confirmed">Confirmed</MenuItem>
                                                        <MenuItem value="Outdated">Outdated</MenuItem>
                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                    </Select>
                                                </TableCell>

                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`View comments for ${term.productName || 'term'}`}
                                                        aria-expanded={expandedTerm === term._id}
                                                        onClick={() => handleExpandClick(term._id)}
                                                    >
                                                        {expandedTerm === term._id ? <ExpandLess /> : <ExpandMore />}
                                                    </IconButton>
                                                    {LANGUAGES.some(
                                                        ({ code }) => term.translationComments?.[code] > 0
                                                    ) && <Comment fontSize="small" color="action" />}
                                                </TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <TableCell
                                                    colSpan={visibleLanguages.length + 3}
                                                    sx={{ p: 0, borderBottom: expandedTerm === term._id ? undefined : 0 }}
                                                >
                                                    <Collapse
                                                        in={expandedTerm === term._id}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <Box sx={{ p: 2 }}>
                                                            <CommentList
                                                                termId={term._id}
                                                                apiKey={process.env.REACT_APP_API_KEY}
                                                            />
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={terms.length}
                        page={currentPage}
                        onPageChange={(event, newPage) => setCurrentPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[rowsPerPage]}
                    />
                </>
            )}
        </Paper>
    );
}

export default TermList;
